'use client';

import { useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useFormikContext } from 'formik';

import { TimerIcon } from 'src/shared/icons';
import { Button } from 'src/shared/ui/Button';

import { useMutationSendSms } from 'src/dal/services/user/userQueries';
import { getApiError } from 'src/shared/utils/getApiError';

import styles from '../../client-component.module.css';

import type { VerificationCodeFormValues } from '../VerificationCodeForm';
import { digitsOnly } from '../utils';

import { ApplicantFormContext } from './ApplicantFormProvider';

const TIMER_DURATION = 120;

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const ResendSmsButton = () => {
  const searchParams = useSearchParams();

  const { values: applicantValues } = useContext(ApplicantFormContext);
  const { setFieldValue, setFieldError, setFieldTouched } = useFormikContext<VerificationCodeFormValues>();

  const [timer, setTimer] = useState<number>(TIMER_DURATION);

  const legalId = searchParams.get('legal_id');

  const resendSmsMutation = useMutationSendSms({
    onSuccess: () => {
      setTimer(TIMER_DURATION);
      setFieldValue('verificationCode', '');
      setFieldError('verificationCode', undefined);
      setFieldTouched('verificationCode', false);
    },
    onError: (error) => {
      setFieldError('verificationCode', getApiError(error));
      setFieldTouched('verificationCode', true, false);
    },
  });

  useEffect(() => {
    if (timer <= 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timer]);

  const handleResendSms = () => {
    if (!legalId || !applicantValues?.phone) {
      return;
    }

    resendSmsMutation.mutate({
      phone: digitsOnly(applicantValues.phone),
      legalId,
    });
  };

  return (
    <Button
      className={styles.resendButton}
      disabled={timer > 0 || resendSmsMutation.isPending}
      leftAddons={timer > 0 ? <TimerIcon /> : undefined}
      stretched
      onClick={handleResendSms}
    >
      {timer > 0 ? `Отправить код повторно (${formatTime(timer)})` : 'Отправить код повторно'}
    </Button>
  );
};
